import { PaginatedDocketItems } from '../../layoutHelper';
import { ensureGridExists } from './gridUtils';
import { Grid, PositionInfo, DocketOverflowsType } from './types';

export const markBottomOnlyColumns = (
  grids: Grid[],
  bottomOnlyColumns: Set<string>,
  colsPerPage: number,
) => {
  bottomOnlyColumns.forEach(key => {
    const [pageStr, colStr] = key.split('-');
    const pageIndex = parseInt(pageStr) - 1;
    const colIndex = parseInt(colStr) - 1;

    ensureGridExists(grids, pageIndex, colIndex, colsPerPage);
    const column = grids[pageIndex]?.columns[colIndex];

    if (column) {
      const [topCell] = column;
      topCell.isReserved = true;
    }
  });
};

export const handleColumnGaps = (
  prevItems: PaginatedDocketItems[],
  usedColumns: Set<string>,
  positionMap: Record<string, PositionInfo>,
  bottomOnlyColumns: Set<string>,
  docketOverflows: DocketOverflowsType,
) => {
  const emptyColumns: { page: number; column: number }[] = [];

  if (prevItems && prevItems.length > 0) {
    prevItems.forEach((page, pageIndex) => {
      if (!page || !page.columns) return;

      const pageNum = pageIndex + 1;
      const maxColumn = page.columns.reduce(
        (max, col) => Math.max(max, col.column),
        0,
      );

      for (let col = 1; col <= maxColumn; col++) {
        const colKey = `${pageNum}-${col}`;
        if (!usedColumns.has(colKey)) {
          emptyColumns.push({ page: pageNum, column: col });
        }
      }
    });
  }

  if (emptyColumns.length === 0) {
    return;
  }

  emptyColumns.sort((a, b) =>
    a.page !== b.page ? a.page - b.page : a.column - b.column,
  );

  emptyColumns.forEach(emptyCol => {
    Object.keys(positionMap).forEach(docketId => {
      const colInfo = positionMap[docketId];
      if (colInfo.page === emptyCol.page && colInfo.column > emptyCol.column) {
        colInfo.column--;
      }
    });

    const updatedBottomOnly = new Set<string>();
    bottomOnlyColumns.forEach(key => {
      const [pageStr, colStr] = key.split('-');
      const page = parseInt(pageStr);
      const col = parseInt(colStr);

      if (page === emptyCol.page && col > emptyCol.column) {
        updatedBottomOnly.add(`${page}-${col - 1}`);
      } else if (!(page === emptyCol.page && col === emptyCol.column)) {
        updatedBottomOnly.add(key);
      }
    });

    bottomOnlyColumns.clear();
    updatedBottomOnly.forEach(key => bottomOnlyColumns.add(key));

    docketOverflows.forEach((positions, docketId) => {
      const updatedPositions = positions.map(
        (pos: { page: number; column: number }) => {
          if (pos.page === emptyCol.page && pos.column > emptyCol.column) {
            return { page: pos.page, column: pos.column - 1 };
          }
          return pos;
        },
      );
      docketOverflows.set(docketId, updatedPositions);
    });
  });
};

export const findNextAvailablePosition = (
  grids: Grid[],
  colsPerPage: number,
  positionsPerPage: number,
  bottomOnlyColumns: Set<string>,
  isLarge: boolean,
  needsFullColumn = false,
) => {
  // Find max used position in a single pass with early termination
  let maxUsedPage = 0;
  let maxUsedColumn = -1;

  // Traverse grids in reverse order to find last used position faster
  for (let pageIdx = grids.length - 1; pageIdx >= 0; pageIdx--) {
    const grid = grids[pageIdx];
    let foundOccupied = false;

    // Traverse columns in reverse to find highest used column
    for (let colIdx = grid.columns.length - 1; colIdx >= 0; colIdx--) {
      const column = grid.columns[colIdx];
      if (column[0].isOccupied || column[1].isOccupied) {
        maxUsedPage = pageIdx;
        maxUsedColumn = colIdx;
        foundOccupied = true;
        break; // Early termination - found last occupied column
      }
    }

    if (foundOccupied) break; // Early termination - found last occupied page
  }

  // Initialize starting position
  let pageIndex = maxUsedPage;
  let colIndex = maxUsedColumn;
  let rowIndex = 0;
  let rowPosition = 0;

  // Handle empty grid case
  if (maxUsedColumn === -1) {
    return {
      pageIndex: 0,
      colIndex: 0,
      rowIndex: 0,
      position: 1,
      rowPosition: 0,
    };
  }

  // Check if we can use bottom half of last column for small items
  if (!isLarge && !needsFullColumn && maxUsedColumn >= 0) {
    ensureGridExists(grids, pageIndex, colIndex, colsPerPage);
    const lastColumn = grids[pageIndex].columns[colIndex];

    if (
      lastColumn[0].isOccupied &&
      !lastColumn[1].isOccupied &&
      !lastColumn[1].isTall
    ) {
      // We can use the bottom half of this column
      return {
        pageIndex,
        colIndex,
        rowIndex: 1,
        position: pageIndex * positionsPerPage + colIndex * 2 + 2,
        rowPosition: 1,
      };
    }
  }

  // Move to next column
  colIndex++;

  // Check if we need to move to next page
  if (colIndex >= colsPerPage) {
    pageIndex++;
    colIndex = 0;
  }

  // Keep track of cells we've checked to avoid infinite loops
  const checkedCells = new Set();

  // Main search loop for finding available position
  while (true) {
    // Ensure grid exists
    ensureGridExists(grids, pageIndex, colIndex, colsPerPage);

    // Get current column
    const column = grids[pageIndex].columns[colIndex];

    // Create a unique key for this cell
    const cellKey = `${pageIndex}-${colIndex}-${rowIndex}`;

    // Check if we've seen this cell already (safety check)
    if (checkedCells.has(cellKey)) {
      // Prevent infinite loop - should never happen, but just in case
      pageIndex++;
      colIndex = 0;
      rowIndex = 0;
      continue;
    }

    checkedCells.add(cellKey);

    // Check bottomOnlyColumns constraint
    const colKey = `${pageIndex + 1}-${colIndex + 1}`;
    const isBottomOnlyColumn = bottomOnlyColumns.has(colKey);

    if (rowIndex === 0 && isBottomOnlyColumn && column[1].isOccupied) {
      // This column is reserved for bottom-only and already has content
      colIndex++;
      if (colIndex >= colsPerPage) {
        pageIndex++;
        colIndex = 0;
      }
      continue;
    }

    // Check if cell is occupied
    if (column[rowIndex].isOccupied || (rowIndex === 1 && column[1].isTall)) {
      // Try bottom cell if we're small and top is occupied
      if (
        rowIndex === 0 &&
        !isLarge &&
        !needsFullColumn &&
        !column[1].isOccupied &&
        !column[1].isTall
      ) {
        rowIndex = 1;
        rowPosition = 1;
        continue;
      }

      // Move to next column
      colIndex++;
      rowIndex = 0;
      rowPosition = 0;

      // Check page boundary
      if (colIndex >= colsPerPage) {
        pageIndex++;
        colIndex = 0;
      }
      continue;
    }

    // Special check for large items
    if ((isLarge || needsFullColumn) && rowIndex === 0) {
      if (column[1].isOccupied && !column[0].items.length) {
        // Can't place large item here as bottom is occupied
        colIndex++;
        if (colIndex >= colsPerPage) {
          pageIndex++;
          colIndex = 0;
        }
        continue;
      }
    }

    // Found an available position
    break;
  }

  // Calculate final position
  const position = pageIndex * positionsPerPage + colIndex * 2 + rowIndex + 1;

  return {
    pageIndex,
    colIndex,
    rowIndex,
    position,
    rowPosition,
  };
};

export const findNextPosition = (
  grids: Grid[],
  positionMap: Record<string, PositionInfo>,
  colsPerPage: number,
  positionsPerPage: number,
  docketOverflows: DocketOverflowsType,
  bottomOnlyColumns: Set<string>,
  isLarge: boolean,
  docketId: string,
) => {
  if (docketId && positionMap[docketId]) {
    const colInfo = positionMap[docketId];
    const pageIndex = colInfo.page - 1;
    const colIndex = colInfo.column - 1;
    const rowIndex = colInfo.rowPosition === 0 ? 0 : 1;

    ensureGridExists(grids, pageIndex, colIndex, colsPerPage);

    const cell = grids[pageIndex].columns[colIndex][rowIndex];
    if (!cell.isOccupied && !cell.isTall) {
      const position =
        pageIndex * positionsPerPage + colIndex * 2 + rowIndex + 1;
      return {
        pageIndex,
        colIndex,
        rowIndex,
        position,
        rowPosition: colInfo.rowPosition,
      };
    }
  }
  if (
    docketOverflows &&
    docketOverflows.has(docketId) &&
    (docketOverflows.get(docketId) ?? []).length > 1
  ) {
    const positions = docketOverflows.get(docketId);
    if (positions && positions.length > 0) {
      const firstPos = positions[0];
      const pageIndex = firstPos.page - 1;
      const colIndex = firstPos.column - 1;

      ensureGridExists(grids, pageIndex, colIndex, colsPerPage);

      const cell = grids[pageIndex].columns[colIndex][0];
      if (!cell.isOccupied) {
        return {
          pageIndex,
          colIndex,
          rowIndex: 0,
          position: pageIndex * positionsPerPage + colIndex * 2 + 1,
          rowPosition: 0,
        };
      }
    }
  }
  return findNextAvailablePosition(
    grids,
    colsPerPage,
    positionsPerPage,
    bottomOnlyColumns,
    isLarge,
  );
};
